import autoTable from 'jspdf-autotable';
import { BasePDFGenerator, PDFGeneratorConfig } from '../reportUtils/base';
import { formatDate, formatMoney } from '../../../../../../lib/functions/utils';
import { getShortString } from '../reportUtils/documentUtils';
import { PayIn, Document, NotRecognizedConciliation } from '../../../../../../lib/types/models';
import { getProductTypeLabel } from '../reportUtils/documentUtils';

type AnyDocument = Document & { [key: string]: any };

interface PayInWithDetails extends PayIn {
    amountConciliated: number; 
    statusTranslated: string;
    bankAccountDestinationName: string;
    bankAccountOriginName: string;
    businessDestinationName: string;
    businessDestinationIdentifier: string;
    businessOriginName: string;
    businessOriginIdentifier: string;
}

interface Business {
    id: number;
    name: string;
    identifier: string;
    startDate: string | null;
    segment: string | null;
  }
  
  interface Stakeholder {
    id: number;
    name: string;
    identifier: string;
    startDate: string | null;
    segment: string | null;
  }
  
  export interface DocumentWithConciliation extends Document {
    payInId: number;
    payInConciliationId: number;
    payInDate: string;
    paidBy: string;
  
    typeEsp: string;
    businessName: string;
    stakeholderName: string;
    amountNet: number;
  
    productType: 'factoring' | 'credit' | 'invoice' | 'confirming';
    rate?: number;
    arrearRate?: number;
    advance?: number;
    paymentDate?: string;
    extensions?: any[];
  
    amountConciliated: number;
    documentAmountDebtBefore: number;
    arrearAmountDebtBefore: number;
    documentAmountDebtAfter: number;
    arrearAmountDebtAfter: number;
    documentAmountConciliated: number;
    arrearAmountConciliated: number;
  
    business?: Business;
    stakeholder?: Stakeholder;
  }


interface PayInReportConfig extends PDFGeneratorConfig {
  payIn: PayInWithDetails;
  documents: DocumentWithConciliation[];
  fncConciliations: NotRecognizedConciliation[];
}

function reduceDocumentsByProperty(
  documents: AnyDocument[],
  property: keyof AnyDocument | string,
  keep: 'highest' | 'lowest' | 'all' = 'all',
) {
  const groupedByFolio = documents.reduce(
    (acc, doc) => {
      if (!acc[doc.folio]) {
        acc[doc.folio] = [];
      }
      acc[doc.folio].push(doc);
      return acc;
    },
    {} as Record<string, AnyDocument[]>,
  );

  return Object.values(groupedByFolio).reduce((total, docs) => {
    if (keep === 'all') {
      return total + docs.reduce((sum, doc) => sum + (doc[property as string] ?? 0), 0);
    }

    const value = docs.reduce(
      (result, doc) => {
        const currentValue = doc[property as string] ?? 0;
        return keep === 'highest'
          ? Math.max(result, currentValue)
          : Math.min(result, currentValue);
      },
      keep === 'highest' ? -Infinity : Infinity,
    );

    return total + value;
  }, 0);
}

export class PayInReportGenerator extends BasePDFGenerator {
  private payIn: PayInWithDetails;
  private documents: DocumentWithConciliation[];
  private fncConciliations: NotRecognizedConciliation[];

  constructor(config: PayInReportConfig) {
    super(config);
    this.payIn = config.payIn;
    this.documents = config.documents;
    this.fncConciliations = config.fncConciliations;
  }

  protected addSummarySection(): number {
    const c1x = 10, c2x = 110, valueOffset = 35, y0 = 30, line = 6;
    let y = y0;
    const payIn = this.payIn;

    this.drawKeyValue('Rut origen', payIn.businessOriginIdentifier, c1x, y, valueOffset);
    this.drawKeyValue('Rut destino', payIn.businessDestinationIdentifier, c2x, y, valueOffset);
    y += line;
    this.drawKeyValue('Nombre origen', payIn.businessOriginName, c1x, y, valueOffset);
    this.drawKeyValue('Nombre destino', getShortString(payIn.businessDestinationName), c2x, y, valueOffset);
    y += line;
    this.drawKeyValue('Banco origen', getShortString(payIn.bankAccountOriginName), c1x, y, valueOffset);
    this.drawKeyValue('Banco destino', getShortString(payIn.bankAccountDestinationName), c2x, y, valueOffset);
    y += line * 2;
    this.drawText(`Resumen operación ID:${payIn.id}`, c1x, y, { size: 12, bold: true });
    y += line;
    this.drawKeyValue('Fecha pago', formatDate(new Date(payIn.createdAt)), c1x, y, valueOffset);
    this.drawKeyValue('Estado', payIn.statusTranslated, c2x, y, valueOffset);
    y += line;
    this.drawKeyValue('Monto pago', formatMoney(payIn.amount), c1x, y, valueOffset);
    this.drawKeyValue('Monto conciliado', formatMoney(payIn.amountConciliated), c2x, y, valueOffset);
    y += line * 2;
    this.drawText('Resumen totales', c1x, y, { size: 12, bold: true });
    y += line;
    const executedDocuments = this.documents.filter((document) => {
      return (
        document.payInConciliationId &&
        document.payInConciliationId > 0 &&
        document.amountConciliated &&
        document.amountConciliated > 0
      );
    });
    const totalConciliated = executedDocuments.reduce((acc, document) => acc + (document.amountConciliated ?? 0), 0);
    const totalDocumentAmountConciliated = executedDocuments.reduce((acc, document) => acc + (document.documentAmountConciliated ?? 0), 0);
    const totalArrearAmountConciliated = executedDocuments.reduce((acc, document) => acc + (document.arrearAmountConciliated ?? 0), 0);
    this.drawKeyValue('Conciliado', formatMoney(totalConciliated), c1x, y, valueOffset);
    this.drawKeyValue('Documentos', formatMoney(totalDocumentAmountConciliated), c2x, y, valueOffset);
    y += line;
    this.drawKeyValue('Pendiente', formatMoney(payIn.amount - totalConciliated), c1x, y, valueOffset);
    this.drawKeyValue('Mora', formatMoney(totalArrearAmountConciliated), c2x, y, valueOffset);
    y += line;
    this.drawText(`* Los valores en las siguientes tablas son calculados respecto a la fecha de pago (${formatDate(new Date(payIn.createdAt))}).`, c1x, y, { size: 7 });
    y += line + 2;
    return y;
  }

  protected addContentSection(): void {
    const startY = this.addSummarySection();
    let lastY = this.addDocumentsTable(startY);
    if (this.fncConciliations.length > 0) {
      lastY = this.addFncTable((this.doc as any).lastAutoTable ? (this.doc as any).lastAutoTable.finalY + 10 : lastY + 10);
    }
    const resumenTitleY = (this.doc as any).lastAutoTable ? (this.doc as any).lastAutoTable.finalY + 10 : lastY + 10;
    this.drawText('Resumen conciliaciones', 10, resumenTitleY, { size: 12, bold: true });
    this.addResumeTable(resumenTitleY + 6);
  }

  private addDocumentsTable(startY: number): number {
    const executedDocuments = this.documents.filter((document) => {
      return (
        document.payInConciliationId &&
        document.payInConciliationId > 0 &&
        document.amountConciliated &&
        document.amountConciliated > 0
      );
    });
    const sortedDocuments = executedDocuments.sort((a, b) => {
      const productOrder: Record<string, number> = { factoring: 1, credit: 2, invoice: 3, confirming: 4 };
      const aOrder = productOrder[String(a.productType)] || 5;
      const bOrder = productOrder[String(b.productType)] || 5;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.folio.localeCompare(b.folio);
    });
    autoTable(this.doc, {
      startY,
      head: [
        [
          'Folio',
          'Tipo',
          'Cliente',
          'Deudor',
          'Pagado por',
          'Monto',
          'Saldo',
          'Conciliado',
          'Restante',
        ],
      ],
      body: sortedDocuments.map((document) => {
        const documentType = getProductTypeLabel(document.productType);
        return [
          document.folio,
          documentType,
          `${document.businessIdentifier}\n${getShortString(document.businessName)}`,
          `${document.stakeholderIdentifier}\n${getShortString(document.stakeholderName)}`,
          document.paidBy,
          `${formatMoney(document.amount)}`,
          `Documento: ${formatMoney(document.documentAmountDebtBefore ?? 0)}\nMora: ${formatMoney(document.arrearAmountDebtBefore ?? 0)}\nTotal: ${formatMoney((document.documentAmountDebtBefore ?? 0) + (document.arrearAmountDebtBefore ?? 0))}`,
          `Documento: ${formatMoney(document.documentAmountConciliated ?? 0)}\nMora: ${formatMoney(document.arrearAmountConciliated ?? 0)}\nTotal: ${formatMoney((document.documentAmountConciliated ?? 0) + (document.arrearAmountConciliated ?? 0))}`,
          `Documento: ${formatMoney(document.documentAmountDebtAfter ?? 0)}\nMora: ${formatMoney(document.arrearAmountDebtAfter ?? 0)}\nTotal: ${formatMoney((document.documentAmountDebtAfter ?? 0) + (document.arrearAmountDebtAfter ?? 0))}`,
        ];
      }),
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
    });
    return (this.doc as any).lastAutoTable ? (this.doc as any).lastAutoTable.finalY : startY + 40;
  }

  private addFncTable(startY: number): number {
    autoTable(this.doc, {
      startY,
      head: [['Id', 'Monto', 'Fecha conciliación FNC', 'Monto conciliado FNC']],
      body: this.fncConciliations.map((fnc) => [
        fnc.id,
        formatMoney(fnc.amount),
        formatDate(new Date(fnc.createdAt)),
        formatMoney(fnc.amount)
      ]),
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
    });
    return (this.doc as any).lastAutoTable ? (this.doc as any).lastAutoTable.finalY : startY + 40;
  }

  private addResumeTable(startY: number): void {
    const executedDocuments = this.documents.filter((document) => {
      return (
        document.payInConciliationId &&
        document.payInConciliationId > 0 &&
        document.amountConciliated &&
        document.amountConciliated > 0
      );
    });
    const totalDocumentAmountDebtBefore = reduceDocumentsByProperty(
      executedDocuments,
      'documentAmountDebtBefore',
      'highest',
    );
    const totalArrearAmountDebtBefore = reduceDocumentsByProperty(
      executedDocuments,
      'arrearAmountDebtBefore',
      'highest',
    );
    const totalDocumentAmountConciliated = reduceDocumentsByProperty(
      executedDocuments,
      'documentAmountConciliated',
      'all',
    );
    const totalArrearAmountConciliated = reduceDocumentsByProperty(
      executedDocuments,
      'arrearAmountConciliated',
      'all',
    );
    const totalDocumentAmountDebtAfter = reduceDocumentsByProperty(
      executedDocuments,
      'documentAmountDebtAfter',
      'lowest',
    );
    const totalArrearAmountDebtAfter = reduceDocumentsByProperty(
      executedDocuments,
      'arrearAmountDebtAfter',
      'lowest',
    );
    const totalFncAmount = this.fncConciliations.reduce(
      (acc, fnc) => acc + fnc.amount,
      0,
    );
    const data = [
      [
        'Documento',
        `${formatMoney(totalDocumentAmountDebtBefore)}`,
        `${formatMoney(totalDocumentAmountConciliated)}`,
        `${formatMoney(totalDocumentAmountDebtAfter)}`,
      ],
      [
        'Mora',
        `${formatMoney(totalArrearAmountDebtBefore)}`,
        `${formatMoney(totalArrearAmountConciliated)}`,
        `${formatMoney(totalArrearAmountDebtAfter)}`,
      ],
      this.fncConciliations.length > 0
        ? [
            'Subtotal documentos',
            `${formatMoney(totalDocumentAmountDebtBefore + totalArrearAmountDebtBefore)}`,
            `${formatMoney(totalDocumentAmountConciliated + totalArrearAmountConciliated)}`,
            `${formatMoney(totalDocumentAmountDebtAfter + totalArrearAmountDebtAfter)}`,
          ]
        : null,
      this.fncConciliations.length > 0
        ? [
            'FNC',
            `${formatMoney(totalFncAmount)}`,
            `${formatMoney(totalFncAmount)}`,
            `${formatMoney(0)}`,
          ]
        : null,
      [
        'Total',
        `${formatMoney(totalDocumentAmountDebtBefore + totalArrearAmountDebtBefore + totalFncAmount)}`,
        `${formatMoney(totalDocumentAmountConciliated + totalArrearAmountConciliated + totalFncAmount)}`,
        `${formatMoney(totalDocumentAmountDebtAfter + totalArrearAmountDebtAfter)}`,
      ],
    ].filter(Boolean) as string[][];
    const headers = ['', 'Saldo', 'Conciliado', 'Restante'];
    autoTable(this.doc, {
      startY,
      head: [headers],
      body: data,
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
      didParseCell: (d: any) => {
        const hasFnc = this.fncConciliations.length > 0;
        const subtotalRowIndex = hasFnc ? 2 : -1;
        const totalRowIndex = data.length - 1;
        if (d.row.index === subtotalRowIndex || d.row.index === totalRowIndex) {
          d.cell.styles.fontStyle = 'bold';
        }
      },
    });
  }
} 